/**
 * @typedef {object} ShareLinkService
 * @property {string} id
 * @property {string} share_link
 * @property {string} link_class
 * @property {string} icon_class
 * @property {undefined|function(string):string} getLink
 * @property {undefined|function(string):object} getData
 */

/**
 * @typedef {object} ShareLinkItem Соцсеть или иная служба, в которой можно поделиться ссылкой
 * @property {string} id service ID
 * @property {string} link Ссылка, на которую надо отправлять данные
 * @property {string} html HTML-код кнопки для показа в нашем модальном окне
 * @property {object|undefined} share_data данные для Web Share API
 */

/**
 * Показывает модальное окно со ссылками для шаринга в соцсетях
 *
 * Параметры:
 *  - url: ссылка, которой надо поделиться в соцсетях
 *  - title: заголовок ссылки (передаётся не во все)
 *  - description: описание ссылки (передаётся не во все)
 *  - image_url: URL изображения ссылки (передаётся не во все)
 *  - services: массив строк или объект. Если не указать, то будут показаны кнопки всех возможных вариантов
 *    Если массив строк, то это просто перечисление нужных ID соцсетей
 *    Если объект, то ключи объекта — ID соцсетей, а значение — объект с индивидуальными настройками:
 *      {
 *        vars: строка или объект — переменные для подстановки в url
 *           - если строка, то url плейсхолдер %s будет заменён на эту строку.
 *             Например: vars: 'facebook'; https://flysa.ru/MOW-2101-ARH/?utm_term=%s => https://flysa.ru/MOW-2101-ARH/?utm_term=facebook
 *           - если объект, то заменены будут плейсхолдеры с названиями ключей
 *             Например: vars: {term: 'vkontakte', medium: 'web'},
 *                       https://flysa.ru/MOW-2101-ARH/?utm_term=%term&utm_medium=%medium =>
 *                       https://flysa.ru/MOW-2101-ARH/?utm_term=vkontakte&utm_medium=web
 *        icon_class: класс иконки, (если надо его заменить). Например 'fab fa-sa-copy-simple'
 *        link_class: класс ссылки (если надо его заменить). Например: 'icon-filled radius-circle social gosuslugi'
 *      }
 *
 *  - modal_options: объект с опциями для модального окна.
 *      - Если не указать вовсе или в объекте не будет ключа 'content', то будет использован встроенный шаблон
 *        модальноо окна. Если в modal_options.content передаёте свой шаблон, то в нём должны быть следующие классы:
 *
 *      - Если в modal_options не будет ключа header_title, то у модального окна будет по умолчанию заголовок
 *        из $.sa.locales.share
 *
 *  service с ID=copylink — это особый сервис, предназначенный для кнопки "скопировать в буфер обмена". Добавляется
 *  по умолчанию и не имеет настроек icon_class, link_class. Если кнопка "копировать в буфер обмена" не нужна,
 *  надо явно запретить её: services: {copylink: false}
 *
 *  service с ID=web_share_api — ещё один особый сервис, позволяющий использовать Web Share API браузера, если
 *  этот API поддерживается браузером. У объекта с параметрами для этого сервиса можно указать ключ 'platform'
 *  ('all', 'desktop', 'mobile') — чтобы иметь возможность ограничить шаринг через этот API для разных устройств.
 *  По умолчанию (если не указано), то platform=mobile
 */
var ShareLink = (function ($) {

    /**
     * @param {undefined|object} options
     * @constructor
     */
    ShareLink = function (options) {
        var that = this;

        options = options || {};

        that.services = options['services'] || [];
        that.url_for_share = options['url'] || window.location.href;
        that.title = options['title'] || null;
        that.description = options['description'] || null;
        that.image_url = options['image_url'] || null;
        that.modal_options = options['modal_options'] || {};

        //
        that.web_share_api_desktop = 'desktop';
        that.web_share_api_mobile = 'mobile'; // default
        that.web_share_api_service_id = 'web_share_api';
        that.copylink_service_id = 'copylink';
        that.known_services = that.__initServiceList();
        // Карта синонимов сервисов.
        // Например, дубликаты названий (tg/telegram ли lj/Livejournal) или что-то подобное, чтоб не дублировались
        that.known_services_aliases = {
            tg: 'telegram'
        };
        that.no_html_services = [that.copylink_service_id, that.web_share_api_service_id];
        that.copy_notifier_text_attribute = 'data-copy-notifier-text';

        that.selectors = {
            service_wrapper: '.js-share-link-services',
            copylink_button: '.js-share-link-copylink',
            copylink_wrapper: '.js-share-link-copylink-wrapper'
        };

        that.classes = {
            modal_content: 'share-link-content',
            modal_services_wrapper: ['share-link-services', 'js-share-link-services'],
            modal_copylink_wrapper: ['share-link-copylink-wrapper', 'js-share-link-copylink-wrapper'],
            modal_service_link: 'share-link-service',
            modal_copylink_button: ['gray', 'button', 'share-link-copylink', 'js-share-link-copylink', 'js-copy', 'js-close-modal'],
            modal_copylink_button_icon: ['fab', 'fa-sa-copy-simple'],
            modal_copylink_button_content_wrapper: 'share-link-copylink-button-content',
            modal_copylink_button_text_wrapper: 'share-link-copylink-button-text-wrapper'
        };

        //
        that.modal = null;
        //
        that.init();
    };

    ShareLink.prototype.init = function () {
        var that = this,
            modalOnOpen = that.modal_options['onOpen'] || null;

        if (!Object.keys(that.services).length) {
            that.services = Object.keys(that.known_services);
        }

        that.services = that.__initShareLinks(that.services);

        if (!that.modal_options.hasOwnProperty('content') || !that.modal_options.content.length) {
            that.modal_options.content = that.__getDefaultModalTemplate();
        }

        if (!that.modal_options.hasOwnProperty('header_title')) {
            that.modal_options.header_title = $.sa.locales['share'] || '';
        }

        if (that.modal_options) {
            that.modal_options.onOpen = function ($wrapper, __that) {
                var social_link,
                    $service_wrapper = $wrapper.find(that.selectors.service_wrapper),
                    copylink_service;

                that.services.filter(service => service.id !== that.copylink_service_id)
                    .forEach(service => {
                        social_link = `<div>${service.html}</div>`;
                        $service_wrapper.append($(social_link));
                    });
                copylink_service = that.services.find(s => s.id === that.copylink_service_id);
                if (copylink_service) {
                    $wrapper.find(that.selectors.copylink_button).attr('data-copy-content', copylink_service.link);
                } else {
                    $wrapper.find(that.selectors.copylink_wrapper).remove();
                }
                $wrapper.on('click', `.${that.classes.modal_service_link}`, function () {
                    __that.close();
                });
                if (modalOnOpen && typeof modalOnOpen === 'function') {
                    modalOnOpen($wrapper, __that);
                }
            }
        }
        //
        that.__showModal();
    };

    ShareLink.prototype.__showModal = function () {
        var that = this,
            web_share_service_idx = that.services.findIndex(s => s.id === that.web_share_api_service_id);

        if (web_share_service_idx !== -1) {
            try {
                navigator.share(that.services[web_share_service_idx].share_data);
            } catch (err) {
            }
            return;
        }

        that.modal = $.uiModal(that.modal_options);
    }

    /**
     * HTML модального окна по умолчанию
     *
     * @return {string}
     * @private
     */
    ShareLink.prototype.__getDefaultModalTemplate = function () {
        var that = this,
            content = document.createElement('div'),
            services_wrapper = document.createElement('div'),
            copylink_wrapper = document.createElement('div'),
            copylink_button = document.createElement('button'),
            copylink_button_content_wrapper = document.createElement('div'),
            copylink_button_text_wrapper = document.createElement('div'),
            copylink_button_icon_wrapper = document.createElement('div'),
            copylink_button_icon = document.createElement('i'),
            container = document.createElement('div');

        content.classList.add(that.classes.modal_content);
        services_wrapper.classList.add(...that.classes.modal_services_wrapper);
        copylink_wrapper.classList.add(...that.classes.modal_copylink_wrapper);
        copylink_button.classList.add(...that.classes.modal_copylink_button);
        copylink_button_icon.classList.add(...that.classes.modal_copylink_button_icon);
        copylink_button.type = 'button';
        copylink_button.setAttribute(that.copy_notifier_text_attribute, $.sa.locales['link_copied']);
        copylink_button_text_wrapper.classList.add(that.classes.modal_copylink_button_text_wrapper);
        copylink_button_text_wrapper.appendChild(document.createTextNode($.sa.locales['copy_link']) || '');
        copylink_button_icon_wrapper.appendChild(copylink_button_icon);
        copylink_button_content_wrapper.classList.add(that.classes.modal_copylink_button_content_wrapper);
        copylink_button_content_wrapper.append(copylink_button_text_wrapper, copylink_button_icon_wrapper);
        copylink_button.appendChild(copylink_button_content_wrapper);
        copylink_wrapper.appendChild(copylink_button);
        content.append(services_wrapper, copylink_wrapper);

        container.appendChild(content);

        return container.innerHTML;
    };

    /**
     * Унифицировать список сервисов, независимо от того, как он указан в параметрах. Получить в результате список объектов.
     * Если сервис 'copylink' не задан, то добавить
     *
     * @param {array|object|boolean} options_services
     * @return {ShareLinkItem[]}
     * @private
     */
    ShareLink.prototype.__initShareLinks = function (options_services) {
        var that = this,
            options_service,
            options_service_id,
            options_service_idx,
            add_copylink = true,
            copylink_disabled = false,
            service_data,
            service_var_idx,
            prepared_services = [], share_link_html;

        for (options_service_idx in options_services) {

            if ((typeof options_services[options_service_idx] === 'object') || (options_services[options_service_idx] === false)) {
                options_service_id = options_service_idx;
                options_service = options_services[options_service_idx];
            } else {
                options_service_id = options_services[options_service_idx];
                options_service = {};
            }

            // Синонимы служб
            if(that.known_services_aliases.hasOwnProperty(options_service_id)) {
                options_service_id = that.known_services_aliases[options_service_id];
            }

            if (options_service_id === that.copylink_service_id) {
                add_copylink = false;
                if (options_service === false) {
                    copylink_disabled = true;
                    continue;
                }
            }

            /** @type {ShareLinkItem} */
            service_data = {id: options_service_id, link: that.url_for_share, html: ''};
            if (options_service['vars'] && typeof options_service['vars'] === 'object') {
                for (service_var_idx in options_service['vars']) {
                    service_data.link = service_data.link.replace(`%${service_var_idx}`, options_service['vars'][service_var_idx]);
                }
            } else if (options_service['vars'] && typeof options_service['vars'] === 'string') {
                service_data.link = service_data.link.replace(`%s`, options_service['vars']);
            } else {
                service_data.link = service_data.link.replace(`%s`, options_service_id);
            }

            // Web Share API?
            if (options_service_id === that.web_share_api_service_id) {
                if (that.__isShareApiAvailable(options_service['platform'])) {
                    var share_data = that.known_services.web_share_api.getData(service_data.link);

                    if (!navigator.canShare(share_data)) {
                        continue;
                    }
                } else {
                    continue
                }
                service_data.share_data = share_data;
            }

            share_link_html = that.__buildShareServiceHtml(service_data, options_service, that.known_services[options_service_id]);
            if (share_link_html) {
                service_data.html = share_link_html;
            }

            prepared_services.push(service_data);
        }

        if (add_copylink) {
            service_data = {id: that.copylink_service_id, link: that.url_for_share, html: ''};
            service_data.link = service_data.link.replace(`%s`, service_data.id);
            prepared_services.push(service_data);
        }

        return prepared_services;
    };

    /**
     * @param {ShareLinkItem} service_data
     * @param {object} service_setting
     * @param {ShareLinkService|undefined} known_service
     * @return string|null
     * @private
     */
    ShareLink.prototype.__buildShareServiceHtml = function (service_data, service_setting, known_service) {
        var that = this, service_icon_class, service_url, service_link_class;

        service_icon_class = '';
        if (service_setting.hasOwnProperty('icon_class')) {
            service_icon_class = `${service_setting['icon_class']}`
        } else if (known_service && known_service.hasOwnProperty('icon_class')) {
            service_icon_class = known_service.icon_class;
        }

        service_url = null;
        if (service_setting.hasOwnProperty('share_link')) {
            service_url = service_setting.share_link.replace('%s', encodeURIComponent(service_data.link))
        } else if (known_service && known_service.hasOwnProperty('getLink')) {
            service_url = known_service.getLink(service_data.link);
        } else {
            return null;
        }

        service_link_class = [];
        if (service_setting.hasOwnProperty('link_class')) {
            service_link_class = Array.isArray(service_setting.link_class) ? service_setting.link_class.slice() : [service_setting.link_class];
        } else if (known_service && known_service.hasOwnProperty('link_class')) {
            service_link_class = [known_service.link_class];
        }
        service_link_class.push(that.classes.modal_service_link);
        service_link_class = service_link_class.join(' ');

        return `<a href="${service_url}" target="_blank" class="${service_link_class}"><i class="${service_icon_class}"></i></a>`
    }

    /**
     * Доступно-ли Web Share API
     *
     * @param {string} platform
     * @return {boolean}
     * @private
     */
    ShareLink.prototype.__isShareApiAvailable = function (platform) {
        var that = this;

        if (!navigator.canShare) {
            // Web Share API is not supported by the browser
            return false;
        }

        platform = platform || that.web_share_api_mobile;

        switch (platform) {
            case that.web_share_api_mobile:
                return $.sa.is_mobile() || $.sa.is_tablet();
            case that.web_share_api_desktop:
                return $.sa.is_desktop();
        }
        return true;
    }

    /**
     * @return {Object.<string, ShareLinkService>}
     * @private
     */
    ShareLink.prototype.__initServiceList = function () {
        var that = this;
        return {
            vk: {
                id: 'vk',
                share_link: 'https://vk.com/share.php',
                link_class: 'icon-filled radius-circle social vk inverse',
                icon_class: 'fab fa-sa-fill-vk',
                getLink(share_url) {
                    var __that = this;
                    var params = {url: share_url, noparse: 'true'};
                    if (that.title) {
                        params.title = that.title;
                    }
                    if (that.description) {
                        params.description = that.description;
                    }
                    if (that.image_url) {
                        params.image = that.image_url;
                    }
                    return __that.share_link + '?' + (new URLSearchParams(params));
                }
            },
            ok: {
                id: 'vk',
                share_link: 'https://connect.ok.ru/offer',
                link_class: 'icon-filled radius-circle social odnoklassniki inverse',
                icon_class: 'fab fa-sa-fill-odnoklassniki',
                getLink(share_url) {
                    var __that = this,
                        params = {url: share_url};

                    if (that.title) {
                        params.title = that.title;
                    }
                    if (that.description) {
                        params.description = that.description;
                    }
                    if (that.image_url) {
                        params.imageUrl = that.image_url;
                    }

                    return __that.share_link + '?' + (new URLSearchParams(params));
                }
            },
            telegram: {
                id: 'telegram',
                share_link: 'https://t.me/share/url',
                link_class: 'icon-filled radius-circle social telegram inverse',
                icon_class: 'fab fa-sa-fill-telegram',
                getLink(share_url) {
                    var __that = this,
                        params = {url: share_url};
                    if (that.description) {
                        params.text = that.description;
                    } else if (that.title) {
                        params.text = that.title;
                    }
                    return __that.share_link + '?' + (new URLSearchParams(params));
                }
            },
            whatsapp: {
                id: 'whatsapp',
                share_link: 'https://wa.me/',
                link_class: 'icon-filled radius-circle social whatsapp inverse',
                icon_class: 'fab fa-whatsapp',
                getLink(share_url) {
                    var __that = this, text = null;
                    if (that.description) {
                        text = that.description;
                    } else if (that.title) {
                        text = that.title;
                    }
                    return __that.share_link +
                        '?text=' +
                        encodeURIComponent((text ? `${text} — ` : '') + share_url);
                }
            },
            email: {
                id: 'email',
                share_link: 'mailto:',
                link_class: 'icon-filled radius-circle social inverse',
                icon_class: 'far fa-envelope',
                getLink(share_url) {
                    var __that = this;
                    var params = {body: (that.description ? `${that.description}\n` : '') + share_url};
                    if (that.title) {
                        params.subject = that.title;
                    }
                    return __that.share_link + '?' + (new URLSearchParams(params));
                }
            },
            web_share_api: {
                id: 'web_share_api',
                default_platform: that.web_share_api_mobile,
                getData(share_url) {
                    var data = {url: share_url};
                    if (that.title) {
                        data.title = that.title;
                    }
                    if (that.description) {
                        data.text = that.description;
                    }
                    return data;
                }
            }
        };
    }

    return ShareLink;
})(jQuery);
